/**
 * Iter132 — NexusTrac Tutorials.
 *
 * Video library every staff user can watch.  Admins upload MP4 videos
 * with a title + category; the backend auto-generates a thumbnail from
 * the first-second frame via ffmpeg.  Non-admin users see tiles, click
 * to play, and that's it — no delete, no upload.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  GraduationCap, UploadCloud, Play, Trash2, Pencil, X,
  Loader2, Video,
} from 'lucide-react';
import { BACKEND_URL } from '../config/canonical';

const TOKEN_KEY = 'nexus_token';
const H = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });

const DEFAULT_CATEGORIES = [
  'Field Reports',
  'Field Events',
  'Field Assignments',
  'Cases',
  'Signing & Onboarding',
  'General',
];

export default function Tutorials() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [canUpload, setCanUpload] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [playing, setPlaying] = useState(null);
  const [activeCat, setActiveCat] = useState('All');

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/tutorials`, { headers: H() });
      if (r.ok) {
        const d = await r.json();
        setItems(d.tutorials || []);
        setCategories(d.categories || DEFAULT_CATEGORIES);
        setCanUpload(!!d.can_upload);
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const pool = activeCat === 'All' ? items : items.filter(t => t.category === activeCat);
    const out = {};
    for (const t of pool) {
      const key = t.category || 'General';
      if (!out[key]) out[key] = [];
      out[key].push(t);
    }
    return out;
  }, [items, activeCat]);

  const removeTutorial = async (t) => {
    if (!window.confirm(`Delete "${t.title}"? This cannot be undone.`)) return;
    try {
      const r = await fetch(`${BACKEND_URL}/api/tutorials/${t.id}`, {
        method: 'DELETE', headers: H(),
      });
      if (!r.ok) throw new Error('Delete failed');
      toast.success('Tutorial deleted');
      load();
    } catch (e) { toast.error(e.message); }
  };

  const renameTutorial = async (t) => {
    const title = window.prompt('New title', t.title);
    if (!title || title === t.title) return;
    try {
      const r = await fetch(`${BACKEND_URL}/api/tutorials/${t.id}`, {
        method: 'PATCH',
        headers: { ...H(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ title }),
      });
      if (!r.ok) throw new Error('Update failed');
      toast.success('Updated');
      load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-5" data-testid="tutorials-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-lg bg-amber-500/15 border border-amber-500/40 flex items-center justify-center">
            <GraduationCap className="w-6 h-6 text-amber-300" />
          </div>
          <div>
            <div className="text-xs text-amber-300 uppercase tracking-wider font-mono">NexusTrac · Learning</div>
            <h1 className="text-2xl font-bold text-white">Tutorials</h1>
            <p className="text-slate-400 text-sm">Short walkthroughs for every NexusTrac workflow.</p>
          </div>
        </div>
        {canUpload && (
          <button onClick={() => setShowUpload(true)}
            data-testid="tutorials-upload-btn"
            className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold px-4 py-2 rounded flex items-center gap-2">
            <UploadCloud className="w-4 h-4" /> Upload Video
          </button>
        )}
      </div>

      {/* Category filter */}
      <div className="flex flex-wrap gap-1.5">
        {['All', ...categories].map(c => (
          <button key={c}
            onClick={() => setActiveCat(c)}
            data-testid={`tut-cat-${c}`}
            className={`px-3 py-1.5 rounded text-xs font-semibold border ${
              activeCat === c
                ? 'bg-amber-500 border-amber-500 text-slate-900'
                : 'bg-slate-900/60 border-slate-700 text-slate-300 hover:bg-slate-800'
            }`}>
            {c}
          </button>
        ))}
      </div>

      {loading && (
        <p className="text-slate-400 flex items-center gap-2 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>
      )}

      {!loading && Object.keys(grouped).length === 0 && (
        <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-10 text-center" data-testid="tutorials-empty">
          <Video className="w-10 h-10 mx-auto text-slate-500 mb-2" />
          <p className="text-slate-400">No tutorials yet.</p>
          {canUpload && (
            <p className="text-slate-500 text-xs mt-1">Click Upload Video to add the first one.</p>
          )}
        </div>
      )}

      {!loading && Object.entries(grouped).map(([cat, rows]) => (
        <div key={cat} className="space-y-2">
          <h2 className="text-sm font-semibold text-amber-300 uppercase tracking-wider font-mono">{cat}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {rows.map(t => (
              <div key={t.id} className="rounded-lg border border-slate-700 bg-slate-900/60 overflow-hidden group hover:border-amber-500/60 transition" data-testid={`tutorial-card-${t.id}`}>
                <button onClick={() => setPlaying(t)} className="relative w-full aspect-video bg-slate-800" data-testid={`tutorial-play-${t.id}`}>
                  {t.thumbnail_url ? (
                    <img src={t.thumbnail_url} alt={t.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Video className="w-10 h-10 text-slate-600" />
                    </div>
                  )}
                  <span className="absolute inset-0 bg-black/40 group-hover:bg-black/60 transition flex items-center justify-center">
                    <Play className="w-10 h-10 text-white drop-shadow-lg" />
                  </span>
                </button>
                <div className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-white line-clamp-2">{t.title}</p>
                      {t.description && <p className="text-xs text-slate-400 mt-0.5 line-clamp-2">{t.description}</p>}
                    </div>
                    {canUpload && (
                      <div className="flex flex-col gap-1 shrink-0">
                        <button onClick={() => renameTutorial(t)} className="text-slate-400 hover:text-white" data-testid={`tutorial-rename-${t.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => removeTutorial(t)} className="text-red-400 hover:text-red-300" data-testid={`tutorial-delete-${t.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {playing && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setPlaying(null)} data-testid="tutorial-player-overlay">
          <div className="relative w-full max-w-4xl bg-slate-900 border border-slate-700 rounded-lg overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-3 border-b border-slate-700">
              <p className="text-white font-semibold truncate pr-4">{playing.title}</p>
              <button onClick={() => setPlaying(null)} className="text-slate-400 hover:text-white" data-testid="tutorial-player-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <video src={playing.video_url} controls autoPlay className="w-full h-auto bg-black" data-testid="tutorial-player-video" />
          </div>
        </div>
      )}

      {showUpload && (
        <UploadModal
          categories={categories}
          onClose={() => setShowUpload(false)}
          onDone={() => { setShowUpload(false); load(); }}
        />
      )}
    </div>
  );
}

function UploadModal({ categories, onClose, onDone }) {
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState(categories[0] || 'General');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const submit = async () => {
    if (!title.trim()) return toast.error('Title is required');
    if (!file) return toast.error('Pick a video file');
    if (!file.type.startsWith('video/')) return toast.error('That is not a video file');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', title.trim());
      fd.append('category', category);
      fd.append('description', description || '');
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${BACKEND_URL}/api/tutorials`);
        xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem(TOKEN_KEY)}`);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300) ? resolve(xhr.responseText) : reject(new Error(`HTTP ${xhr.status}: ${xhr.responseText}`));
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(fd);
      });
      toast.success('Tutorial uploaded');
      onDone();
    } catch (e) {
      toast.error(e.message);
    } finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="tutorial-upload-overlay">
      <div className="w-full max-w-lg bg-slate-900 border border-slate-700 rounded-lg">
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <p className="text-white font-semibold">Upload Tutorial Video</p>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="block text-xs text-slate-400 mb-1">Title *</label>
            <input value={title} onChange={e => setTitle(e.target.value)}
              placeholder="e.g. How to log a field event"
              className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-sm text-white"
              data-testid="tut-upload-title" />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Category</label>
            <select value={category} onChange={e => setCategory(e.target.value)}
              className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-sm text-white"
              data-testid="tut-upload-cat">
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Description</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)}
              rows={2}
              className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-sm text-white"
              data-testid="tut-upload-desc" />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Video File (MP4) *</label>
            <input type="file" accept="video/*" onChange={e => setFile(e.target.files?.[0] || null)}
              className="w-full text-sm text-slate-300" data-testid="tut-upload-file" />
            {file && <p className="text-xs text-slate-500 mt-1">{file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB</p>}
          </div>
          {uploading && (
            <div>
              <div className="h-2 bg-slate-800 rounded overflow-hidden">
                <div className="h-full bg-amber-500" style={{ width: `${progress}%` }} />
              </div>
              <p className="text-xs text-slate-400 mt-1 font-mono">{progress}%</p>
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-700">
          <button onClick={onClose} disabled={uploading}
            className="px-3 py-2 rounded border border-slate-600 text-slate-200 text-sm">
            Cancel
          </button>
          <button onClick={submit} disabled={uploading}
            data-testid="tut-upload-submit"
            className="px-4 py-2 rounded bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold text-sm flex items-center gap-2">
            {uploading && <Loader2 className="w-4 h-4 animate-spin" />}
            {uploading ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );
}
